import { Translator } from '@/types/ApiResponseTypes'
import { isMobileUsageResponse, mobileUsageResponseUsage } from '@/types/mobile/MobileUsageResponse'
import {
  MobileUsage,
  MobileUsageData,
  MobileUsageSummaryType,
  MobileUsageType,
  MobileUsageTypeDetail,
} from '@/types/mobile/MobileUsageType'

class MobileUsageResponseTranslator implements Translator<MobileUsage> {
  baseData: any = {
    national: { voice: 0, data: 0, sms: 0, paymentService: 0 },
    roaming: { voice: 0, data: 0, sms: 0, paymentService: 0 },
    international: { voice: 0, data: 0, sms: 0, paymentService: 0 },
    premium: { voice: 0, data: 0, sms: 0, paymentService: 0 },
    roamingOutOfEU: { voice: 0, data: 0, sms: 0, paymentService: 0 },
  }
  dataTypes = ['national', 'roaming', 'international', 'premium', 'roamingOutOfEU']

  convert(response: any): MobileUsage {
    const mobileUsage: MobileUsage = {} as MobileUsage
    if (isMobileUsageResponse(response)) {
      mobileUsage.dateFrom = 'dateFrom' in response ? new Date(Date.parse(response.dateFrom)) : new Date()
      mobileUsage.dateTo = 'dateFrom' in response ? new Date(Date.parse(response.dateTo)) : new Date()
      mobileUsage.period = 'period' in response ? response.period : 'day'
      mobileUsage.subscriber = 'subscriber' in response ? response.subscriber : ''

      if (Array.isArray(response.usage)) {
        const usagedata: MobileUsageData[] = []
        response.usage.forEach((usage: mobileUsageResponseUsage) => {
          usagedata.push(this.convertToMobileUsageModel(usage))
        })
        mobileUsage.data = usagedata
      } else {
        mobileUsage.data = [this.convertToMobileUsageModel(response.usage)]
      }
      mobileUsage.summary = this.calcSummary(mobileUsage.data, 'amount')
      mobileUsage.summaryCost = this.calcSummary(mobileUsage.data, 'cost')
      mobileUsage.globalSummary = this.calcGlobalSummary(mobileUsage.summary)
      mobileUsage.globalSummaryCost = this.calcGlobalSummary(mobileUsage.summaryCost)
    }
    return mobileUsage
  }

  calcSummary(usage: Array<MobileUsageData>, sumType: 'cost' | 'amount'): MobileUsageSummaryType {
    const summary: MobileUsageSummaryType = {
      national: { voice: 0, data: 0, sms: 0, mms: 0, paymentService: 0 },
      roaming: { voice: 0, data: 0, sms: 0, mms: 0, paymentService: 0 },
      international: { voice: 0, data: 0, sms: 0, mms: 0, paymentService: 0 },
      premium: { voice: 0, data: 0, sms: 0, mms: 0, paymentService: 0 },
      roamingOutOfEU: { voice: 0, data: 0, sms: 0, mms: 0, paymentService: 0 },
    }
    if (usage != undefined) {
      for (const k in usage) {
        for (const sk in this.dataTypes) {
          summary[this.dataTypes[sk] as keyof MobileUsageType].data +=
            usage[k].usage[this.dataTypes[sk] as keyof MobileUsageType].data[sumType]
          summary[this.dataTypes[sk] as keyof MobileUsageType].voice +=
            usage[k].usage[this.dataTypes[sk] as keyof MobileUsageType].voice[sumType]
          summary[this.dataTypes[sk] as keyof MobileUsageType].sms +=
            usage[k].usage[this.dataTypes[sk] as keyof MobileUsageType].sms[sumType]
          summary[this.dataTypes[sk] as keyof MobileUsageType].mms +=
            usage[k].usage[this.dataTypes[sk] as keyof MobileUsageType].mms[sumType]
          summary[this.dataTypes[sk] as keyof MobileUsageType].paymentService +=
            usage[k].usage[this.dataTypes[sk] as keyof MobileUsageType].paymentService[sumType]
        }
      }
    }
    return summary
  }
  calcGlobalSummary(usage: MobileUsageSummaryType): MobileUsageTypeDetail {
    const summary = { voice: 0, data: 0, sms: 0, mms: 0, paymentService: 0 }
    if (usage != undefined) {
      for (const sk in this.dataTypes) {
        summary.data += usage[this.dataTypes[sk] as keyof MobileUsageType].data
        summary.voice += usage[this.dataTypes[sk] as keyof MobileUsageType].voice
        summary.sms += usage[this.dataTypes[sk] as keyof MobileUsageType].sms
        summary.mms += usage[this.dataTypes[sk] as keyof MobileUsageType].mms
        summary.paymentService += usage[this.dataTypes[sk] as keyof MobileUsageType].paymentService
      }
    }
    return summary
  }

  convertToMobileUsageModel(usage: mobileUsageResponseUsage): MobileUsageData {
    const usagedata: any = { ...this.baseData }

    let summaryData = 0,
      summeryVoice = 0,
      summerySms = 0,
      summerymms = 0,
      summaryPaymentServices = 0
    let summaryDataCost = 0,
      summeryVoiceCost = 0,
      summerySmsCost = 0,
      summeryMmsCost = 0,
      summaryPaymentServicesCost = 0

    for (const key in usage) {
      if (key == 'date') continue
      if (this.dataTypes.includes(key)) {
        const usg = usage[key as keyof mobileUsageResponseUsage]
        if (typeof usg == 'object' && 'data' in usg) {
          if (key as keyof MobileUsageType) {
            usagedata[key] = {
              data: usg.data,
              voice: usg.voice,
              sms: usg.sms,
              mms: usg.mms,
              paymentService: usg.paymentService,
            }
            summaryData += usg.data.amount
            summeryVoice += usg.voice.amount
            summerySms += usg.sms.amount
            summerymms += usg.mms.amount
            summaryPaymentServices += usg.paymentService.amount

            summaryDataCost += usg.data.cost
            summeryVoiceCost += usg.voice.cost
            summerySmsCost += usg.sms.cost
            summeryMmsCost += usg.mms.cost
            summaryPaymentServicesCost += usg.paymentService.cost
          }
        }
      }
    }
    return {
      date: usage.date,
      usage: usagedata,
      summary: {
        data: summaryData,
        voice: summeryVoice,
        sms: summerySms,
        mms: summerymms,
        paymentService: summaryPaymentServices,
      },
      summaryCost: {
        data: summaryDataCost,
        voice: summeryVoiceCost,
        sms: summerySmsCost,
        mms: summeryMmsCost,
        paymentService: summaryPaymentServicesCost,
      },
    }
  }
}
export default new MobileUsageResponseTranslator()
